/**
 * Generic WebAudio one-shot/loop playback against a `BufferCache` — "play this cue once" and
 * "start this cue looping its steady region," with no per-caller policy (which cue, when, how
 * loud) baked in. Split out of `engine.ts` purely to keep that file under the project's
 * line-count gate (`docs/engineering/lessons.md`) — every decision here was already `engine.ts`'s
 * own, unchanged by the move.
 */

import { loopRegionFor } from "./continuous-params.ts";
import type { BufferCache } from "./buffer-cache.ts";
import { cuePath, SIMSNDS_CUES, type SfxCueId } from "./simsnds-cues.ts";

export interface LoopHandle {
  readonly gainNode: GainNode;
  source: AudioBufferSourceNode | null;
}

/**
 * Creates a `bus`-connected gain node immediately (synchronous — safe to set `.gain` right away)
 * and starts the loop once its buffer decodes, looping only `continuous-params.ts`'s
 * `loopRegionFor` region (the source still plays its attack once on the first pass, then repeats
 * only the steady tail) rather than the whole, decaying buffer.
 */
export function startLoop(
  context: AudioContext,
  cache: BufferCache<AudioBuffer>,
  cueId: SfxCueId,
  bus: GainNode,
  isDisposed: () => boolean,
): LoopHandle {
  const gainNode = context.createGain();
  gainNode.gain.value = 0;
  gainNode.connect(bus);
  const handle: LoopHandle = { gainNode, source: null };
  void cache
    .load(cuePath(cueId))
    .then((buffer) => {
      if (isDisposed()) return;
      const source = context.createBufferSource();
      source.buffer = buffer;
      const region = loopRegionFor(SIMSNDS_CUES[cueId]);
      source.loop = true;
      source.loopStart = region.startS;
      source.loopEnd = region.endS;
      source.connect(gainNode);
      source.start();
      handle.source = source;
    })
    .catch(() => {
      // A missing/undecodable baked cue leaves the loop silent (gain stays 0) rather than throwing.
    });
  return handle;
}

export function playOneShot(
  context: AudioContext,
  cache: BufferCache<AudioBuffer>,
  cueId: SfxCueId,
  bus: GainNode,
  rate: number,
  isDisposed: () => boolean,
  /** 0..1 extra attenuation on top of `bus`'s own gain — e.g. a flak burst's distance falloff. `1` (the default) changes nothing for every existing caller. */
  gain = 1,
  /** Called once the source finishes (or the load fails) — a caller tracking concurrency (flak bursts' `FLAK_BURST_MAX_CONCURRENT`) frees its slot here rather than guessing a duration. */
  onEnded?: () => void,
): void {
  void cache
    .load(cuePath(cueId))
    .then((buffer) => {
      if (isDisposed()) return onEnded?.();
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = rate;
      const gainNode = context.createGain();
      gainNode.gain.value = gain;
      gainNode.connect(bus);
      source.connect(gainNode);
      source.addEventListener("ended", () => {
        source.disconnect();
        gainNode.disconnect();
        onEnded?.();
      });
      source.start();
    })
    .catch(() => {
      // Same policy as loops: a bad fetch/decode just means that one cue doesn't play.
      onEnded?.();
    });
}
